import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, from, of } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  map,
  mergeMap,
  switchMap,
  take,
  toArray
} from 'rxjs/operators';

import { isNil } from '@gfe/core/utils/shared-utils';

import { RegistrationRepairScope } from '@core/models/entities/entity-visualization-interface';
import { UserEntityRegistrations } from '@core/models/entities/implementations/user-entity-registrations';
import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import {
  FilterConnector,
  PlanFilter
} from '@core/models/entity/entity-selector-request.model';
import { EntityModificationService } from '@core/services/entity-modification/entity-modification.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { entityModified } from '@core/store/data-visualization/data-visualization.actions';

import { UidFilterCondition } from '@shared/components/filter-query-builder/filter.models';
import { uidListCondition } from '@shared/utils/filter-condition.utils';

import {
  REGISTRATION_KINDS,
  RegistrationRepairOutcome,
  ResourceRegistrationsByKind,
  RoutePatch
} from './registration-repair.model';
import { overlayWrittenRoute, planRegistrationRepair } from './registration-repair.planner';

/**
 * Matches the lowercase `entityType` every other route modification action uses (see
 * `user-route-v1.ts`'s `getModificationActions`). The refresh listener that reloads an open view
 * filters on an exact match against that value, so drifting from it here would silently stop
 * refreshing routes after a repair.
 */
const ROUTE_ENTITY_TYPE = 'route';
const ROUTE_VERSION_NAME = 'UserRoute';

/** Sibling repairs are independent PATCHes; a saturated fleet can produce a dozen at once. */
const REPAIR_WRITE_CONCURRENCY = 4;

function incomplete(): RegistrationRepairOutcome {
  return { repaired: 0, unrepaired: [], incomplete: true };
}

function nothingToRepair(): RegistrationRepairOutcome {
  return { repaired: 0, unrepaired: [], incomplete: false };
}

function countPatchedRegistrations(patch: RoutePatch): number {
  return REGISTRATION_KINDS.filter(
    (descriptor) => !isNil(patch[descriptor.registrationField])
  ).length;
}

/**
 * Fills the registrations a reassignment left unassigned, on the resources whose occupancy it
 * moved.
 *
 * It runs after the route PATCH has landed, so no failure here may reject: reporting an error
 * would tell the planner nothing changed when the route did. Every failure degrades to
 * `incomplete`, which is today's behaviour — a route with an unassigned registration.
 */
@Injectable({ providedIn: 'root' })
export class RouteRegistrationRepairService {
  private readonly selectorsService = inject(SelectorsService);
  private readonly entityModificationService = inject(EntityModificationService);
  private readonly store = inject(Store);

  repair$(
    scope: RegistrationRepairScope,
    writtenFields: Record<string, unknown>,
    dataSpaceId: number | undefined,
    planFilter: PlanFilter | undefined
  ): Observable<RegistrationRepairOutcome> {
    if (isNil(dataSpaceId)) return of(incomplete());

    const conditions = this.buildScopeConditions(scope);
    if (conditions.length === 0) return of(nothingToRepair());

    return this.selectorsService
      .selectAllData$<UserRouteV1Model>({
        entityType: ROUTE_ENTITY_TYPE,
        entityTypeVersion: ROUTE_VERSION_NAME,
        dataSpaceId,
        planFilter,
        filters: {
          rootGroup: {
            id: 'registration-repair-root',
            connector: FilterConnector.OR,
            conditions
          }
        },
        // Paging has no stable order without this: a concurrent write could shift a row across
        // pages and drop it from every page, and a dropped sibling looks like a free slot to the
        // planner — which would then hand that slot out a second time.
        orderBy: [{ field: 'uid', order: 'asc' }]
      })
      .pipe(
        take(1),
        switchMap((result) => {
          // A page count that does not add up to `total` means the snapshot itself is not
          // trustworthy, regardless of ordering; degrade rather than risk fabricating a
          // double-booking.
          if (result.items.length !== result.total) {
            return of(incomplete());
          }

          const routes = overlayWrittenRoute(result.items, writtenFields);
          // Every condition that could match the edited route was just sent, so its absence
          // means the snapshot cannot be trusted to be exhaustive for the rest either.
          if (!routes.some((route) => route.uid === scope.routeUid)) {
            return of(incomplete());
          }

          return this.loadResources$(scope, dataSpaceId, planFilter).pipe(
            switchMap((resources) => {
              const { patches, unrepaired } = planRegistrationRepair(
                routes,
                resources,
                scope
              );
              return this.writePatches$(patches).pipe(
                map(({ repaired, failed }) => ({
                  repaired,
                  unrepaired,
                  // A rejected patch leaves a route unassigned, so the pass did not complete.
                  incomplete: failed > 0
                }))
              );
            })
          );
        }),
        catchError(() => of(incomplete()))
      );
  }

  private buildScopeConditions(scope: RegistrationRepairScope): UidFilterCondition[] {
    return REGISTRATION_KINDS.filter(
      (descriptor) => scope[descriptor.scopeField].length > 0
    ).map((descriptor) =>
      uidListCondition(
        `registration-repair-${descriptor.kind}`,
        descriptor.resourceField,
        scope[descriptor.scopeField],
        descriptor.entityType
      )
    );
  }

  private loadResources$(
    scope: RegistrationRepairScope,
    dataSpaceId: number,
    planFilter: PlanFilter | undefined
  ): Observable<ResourceRegistrationsByKind> {
    const versionNameAndUidsPerType: Record<string, [string, string[]]> = {};
    REGISTRATION_KINDS.forEach((descriptor) => {
      const uids = scope[descriptor.scopeField];
      if (uids.length > 0) {
        versionNameAndUidsPerType[descriptor.entityType] = [descriptor.versionName, uids];
      }
    });

    return this.selectorsService
      .fetchAuxiliaryData$({ versionNameAndUidsPerType }, dataSpaceId, planFilter)
      .pipe(
        take(1),
        map((bundle) => {
          const resources: ResourceRegistrationsByKind = {
            vehicle: new Map(),
            trailer: new Map(),
            driver: new Map()
          };

          REGISTRATION_KINDS.forEach((descriptor) => {
            const items = (bundle.entitiesArrayPerVersionName[descriptor.versionName] ??
              []) as { uid: string; registrations?: UserEntityRegistrations }[];
            items.forEach((item) =>
              resources[descriptor.kind].set(item.uid, item.registrations)
            );
          });

          return resources;
        })
      );
  }

  /**
   * Resolves to how many registrations were written (not how many routes were patched — a route
   * repaired for both its vehicle and its trailer is one patch but two registrations) and how
   * many patches did not land, and never rejects.
   */
  private writePatches$(
    patches: RoutePatch[]
  ): Observable<{ repaired: number; failed: number }> {
    if (patches.length === 0) return of({ repaired: 0, failed: 0 });

    return from(patches).pipe(
      mergeMap(
        (patch) =>
          this.entityModificationService
            .saveEntityData$(
              ROUTE_ENTITY_TYPE,
              ROUTE_VERSION_NAME,
              () => patch as unknown as Record<string, unknown>
            )
            .pipe(
              map(() => countPatchedRegistrations(patch)),
              // An unresolvable transformer completes without emitting, which wrote nothing
              // just as surely as a rejection did.
              defaultIfEmpty(0),
              catchError(() => of(0))
            ),
        REPAIR_WRITE_CONCURRENCY
      ),
      toArray(),
      map((results) => {
        const repaired = results.reduce((sum, count) => sum + count, 0);
        const failed = results.filter((count) => count === 0).length;
        if (repaired > 0) {
          // Open views read stale values until told otherwise; one refresh once the writes
          // settled avoids a refetch per patch.
          this.store.dispatch(entityModified({ entityType: ROUTE_ENTITY_TYPE }));
        }
        return { repaired, failed };
      })
    );
  }
}
